/**
 * Sales Control › References switchboard config (Access frmCtrlSalesMainSubFormReferences
 * "Go to Page:" combo — 5 page RIENG cua Outbound). Column field keys MUST match the
 * backend SQL aliases in `sale_control_reference_pg._SALE_CONTROL_REFERENCE_SPECS` and the
 * baseline `sale_control_columns.py` (mode `ref-<pageKey>`).
 */
import type {
  ReferenceColumnConfig,
  ReferenceFilterKind,
  ReferencePageConfig,
  ReferenceValueKind,
} from '@/components/references/types'

export type SaleControlReferencePageKey =
  | 'ServiceInvoices'
  | 'ValueUpdates'
  | 'Annex'
  | 'Delivery'
  | 'Fixation'

// Types unified vào module dùng chung `@/components/references`.
export type SaleControlRefValueKind = ReferenceValueKind
export type SaleControlRefColumn = ReferenceColumnConfig
export type SaleControlRefPageConfig = ReferencePageConfig<SaleControlReferencePageKey>

// Fixation fix1..fix30 (Date Fix N / Lots Fixed N / Level Fixed N).
const FIXATION_FIX_COLS: SaleControlRefColumn[] = Array.from({ length: 30 }, (_, i) => i + 1).flatMap((n) => [
  { field: `fix${n}_date`, headerName: `Date Fix ${n}`, kind: 'date' },
  { field: `fix${n}_lots_fixed`, headerName: `Lots Fixed ${n}`, kind: 'number', precision: 0 },
  { field: `fix${n}_level_fixed`, headerName: `Level Fixed ${n}`, kind: 'number', precision: 2 },
])

export const SALE_CONTROL_REFERENCE_PAGE_CONFIGS: Record<SaleControlReferencePageKey, SaleControlRefPageConfig> = {
  ServiceInvoices: {
    pageKey: 'ServiceInvoices',
    label: 'Service Invoices',
    sourceForm: 'frmCtrlSalesQryInvoice_SuggestInvoice',
    sourceQuery: 'CtrlSalesQryInvoice_SuggestInvoice',
    // Thứ tự cột theo TabIndex của datasheet Access (form single-column, ColumnOrder
    // không set → TabIndex là thứ tự hiển thị). INV-Status ở giữa, KHÔNG ở cuối.
    columns: [
      { field: 'service_code', headerName: 'ServiceCode' },
      { field: 'service_company', headerName: 'ServiceCompany', proper: true, wrap: true, width: 220 },
      { field: 'inv_no_merge', headerName: 'INV-No' },
      { field: 'quantity', headerName: 'INV-Quantity', kind: 'number', precision: 2 },
      { field: 'price', headerName: 'INV-Price', kind: 'number', precision: 2 },
      { field: 'trang_thai_kiem_tra', headerName: 'INV-CheckingStatus' },
      { field: 'ngay_hoa_don', headerName: 'INV-Date', kind: 'date' },
      { field: 'total_amount', headerName: 'TotalAmount (after tax)', kind: 'number', precision: 2 },
      { field: 'total_discount_amount', headerName: 'DiscountAmount', kind: 'number', precision: 2 },
      { field: 'total_amount_without_vat', headerName: 'TotalAmount', kind: 'number', precision: 2 },
      { field: 'total_vat_amount', headerName: 'VATAmount', kind: 'number', precision: 2 },
      { field: 'product_note', headerName: 'INV-Note', wrap: true, width: 200 },
      { field: 'loai_hoa_don', headerName: 'INV-Type' },
      { field: 'trang_thai_hoa_don', headerName: 'INV-Status' },
      { field: 'phan_loai_chung_tu', headerName: 'VoucherType' },
      { field: 'trang_thai_lap_chung_tu', headerName: 'VoucherStatus' },
      { field: 'loai_tien', headerName: 'Currency' },
      { field: 'refno_finance', headerName: 'VoucherNo' },
      { field: 'inv_tab', headerName: 'INVTab' },
    ],
  },
  ValueUpdates: {
    pageKey: 'ValueUpdates',
    label: 'Value Updates',
    sourceForm: 'frmCtrlSalesQryRefValueUpdate',
    sourceQuery: 'CtrlSalesQryValueUpdate',
    columns: [
      { field: 'date', headerName: 'Date', kind: 'date' },
      { field: 'code', headerName: 'Code' },
      { field: 'description', headerName: 'Description', wrap: true, width: 220 },
      { field: 'premium_vs_g2_5percent', headerName: 'Premium vs. G2 5%', kind: 'number', precision: 2, noTotal: true },
      { field: 'reason_of_change', headerName: 'Reason of change', wrap: true, width: 220 },
      // Updated Date = COALESCE(updated_date, created_date) là timestamp → format
      // datetime DD-MMM-YYYY HH:mm (KHÔNG dùng UPDATED_COLS date-only chung).
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime' },
      { field: 'person_update', headerName: 'Updated Person' },
    ],
  },
  Annex: {
    pageKey: 'Annex',
    // Copy 1:1 bảng dữ liệu của /sales/contract/amendment (SaleAmendmentListView):
    // cùng cột / thứ tự / label / format. Label page đổi "Annex" → "Amendment".
    label: 'Amendment',
    sourceForm: 'frmCtrlSalesQryRefAnnex',
    sourceQuery: 'ContractSalesQryAnnex',
    columns: [
      // id: number căn phải nhưng KHÔNG cộng tổng (khớp NON_SUM_TOTAL_KEYS bên amendment).
      { field: 'id', headerName: 'ID', kind: 'number', precision: 0, noTotal: true },
      { field: 'annex_no', headerName: 'Amendment No.' },
      { field: 'annex_date', headerName: 'Amendment Date.', kind: 'date' },
      { field: 'contract_no', headerName: 'Contract Ref' },
      { field: 'cp_code', headerName: 'Supplier Code' },
      { field: 'name', headerName: 'Supplier', proper: true, wrap: true, width: 200 },
      { field: 'content_before_change', headerName: 'Before change', wrap: true, width: 360 },
      { field: 'content_after_change', headerName: 'After change', wrap: true, width: 360 },
      { field: 'note', headerName: 'Note', wrap: true, width: 300 },
      { field: 'price', headerName: 'Price', kind: 'number', precision: 2 },
      { field: 'quantity', headerName: 'Quantity', kind: 'number', precision: 2 },
      // Updated Date = timestamp → datetime DD-MMM-YYYY HH:mm (như amendment page).
      { field: 'date_update', headerName: 'Updated Date', kind: 'datetime' },
      { field: 'person_update', headerName: 'Updated Person' },
    ],
  },
  Delivery: {
    pageKey: 'Delivery',
    label: 'Delivery',
    sourceForm: 'frmCtrlSalesQryRefDelivery',
    sourceQuery: 'CtrlSalesQryDelivery',
    // Thứ tự cột theo datasheet Access frmCtrlSalesQryRefDelivery (TabIndex) —
    // khớp baseline `ref-Delivery`. date_update/person_update nằm xen giữa (vị
    // trí 16/18), KHÔNG ở cuối → inline thay vì UPDATED_COLS.
    columns: [
      { field: 'gdn_id', headerName: 'GDN ID' },
      { field: 'bags_on_truck', headerName: 'Bags on truck', kind: 'number', precision: 0 },
      { field: 'date_out', headerName: 'Date out', kind: 'date' },
      { field: 'net_real_weight', headerName: 'Net real weight', kind: 'number', precision: 2 },
      { field: 'do_no', headerName: 'DO No.' },
      { field: 'deduction_rate', headerName: 'Deduction rate', kind: 'number', precision: 2 },
      { field: 'from_warehouse', headerName: 'From warehouse', wrap: true, width: 180 },
      { field: 'supervisor', headerName: 'Supervisor' },
      { field: 'to_warehouse', headerName: 'To warehouse', wrap: true, width: 180 },
      { field: 'bag_at_buyer', headerName: 'Bag at buyer', kind: 'number', precision: 0 },
      { field: 'lot_name_buyer', headerName: "Lot name's buyer", wrap: true, width: 200 },
      { field: 'net_weight_at_buyer', headerName: 'Net weight at buyer', kind: 'number', precision: 2 },
      { field: 'lot_id', headerName: 'Lot ID' },
      { field: 'deduction_at_buyer', headerName: 'Deduction at buyer', kind: 'number', precision: 2 },
      { field: 'stack_id', headerName: 'Stack ID' },
      { field: 'date_update', headerName: 'Updated Date', kind: 'date' },
      { field: 'package', headerName: 'Package' },
      { field: 'person_update', headerName: 'Updated Person' },
      { field: 'number_theory_of_bag', headerName: 'Number theory of bag', kind: 'number', precision: 0 },
      { field: 'real_bags', headerName: 'Real bags', kind: 'number', precision: 0 },
      { field: 'truck_no', headerName: 'Truck No.' },
      { field: 'package_to_truck', headerName: 'Package to Truck' },
      { field: 'gross_weight', headerName: 'Gross Weight', kind: 'number', precision: 2 },
    ],
  },
  Fixation: {
    pageKey: 'Fixation',
    label: 'PTBF Fixation (Sale)',
    sourceForm: 'frmCtrlSalesQryRefFixation',
    sourceQuery: 'ContractSalesQryPTBFFixation',
    // Thứ tự cột theo datasheet Access frmCtrlSalesQryRefFixation (TabIndex) —
    // khớp baseline `ref-Fixation`: upload_txt/contract_sale/release_date ở đầu,
    // txt_cus_sign trước khối fix. Số căn phải auto (kind number); mtons/ptbf_diffs/
    // final_price 2 chữ số thập phân, no_of_lots/balance_lots_tbf số nguyên.
    columns: [
      { field: 'upload_txt', headerName: 'Is Uploaded?' },
      { field: 'contract_sale', headerName: 'Contract No' },
      { field: 'release_date', headerName: 'ReleaseDate', kind: 'date' },
      { field: 'buyers_ref', headerName: "Buyer's Ref" },
      { field: 'cp_code', headerName: 'Client Ref' },
      { field: 'name', headerName: 'Client Name', proper: true, wrap: true, width: 260 },
      { field: 'quality', headerName: 'Grade' },
      { field: 'description', headerName: 'Description', wrap: true, width: 320 },
      { field: 'mtons', headerName: 'Mtons', kind: 'number', precision: 2 },
      { field: 'no_of_lots', headerName: 'No. of Lots', kind: 'number', precision: 0 },
      { field: 'ptbf_diffs', headerName: 'PTBF Diffs', kind: 'number', precision: 2 },
      { field: 'futures_month', headerName: 'Futures Month' },
      { field: 'balance_lots_tbf', headerName: 'Balance Lots TBF', kind: 'number', precision: 0 },
      { field: 'final_price', headerName: 'Final Price', kind: 'number', precision: 2 },
      { field: 'txt_cus_sign', headerName: 'Customer Signed?' },
      ...FIXATION_FIX_COLS,
    ],
  },
}
